import { type Shadows, createTheme, responsiveFontSizes } from '@mui/material/styles'
// Registers the @mui/x-date-pickers slots (MuiPickersDay, MuiDateCalendar, etc.)
// on the theme's `components` type so they can be themed below.
import type {} from '@mui/x-date-pickers/themeAugmentation'

const fontFamily = '"Slussen"'

// A dropdown's selected option — the JS twin of the `dropdown-option-selected` SCSS mixin.
const selectedOptionStyle = {
  backgroundColor: 'var(--action-accent-subtle)',
  boxShadow: 'inset var(--border-width-indicator) 0 0 var(--action-accent)',
  fontWeight: 500,
}

// Material UI's 25 elevation steps on the three elevation tokens, so a default Card, AppBar or
// Menu shadow themes with the rest of the app.
const SHADOWS = Array.from({ length: 25 }, (_, step) => {
  if (step === 0) return 'none'
  if (step <= 2) return 'var(--elevation-raised)'
  return step <= 8 ? 'var(--elevation-overlay)' : 'var(--elevation-modal)'
}) as Shadows

// The JS twin of the `focus-glow` SCSS mixin.
const FOCUS_GLOW = {
  outline: 'var(--border-width-focus) solid transparent',
  boxShadow: 'var(--focus-glow)',
}

// Floating surfaces carry elevation and no outline; shadows read weakly on dark grounds, so dark mode adds an edge.
const floatingSurface = (elevation: string, radius: string) => ({
  boxShadow: elevation,
  borderRadius: radius,
  ':root[data-color-mode="dark"] &': {
    border: 'var(--border-width-default) solid var(--border-subtle)',
  },
})

// The layer tokens in tokens.scss, as numbers: MUI needs numbers for its zIndex scale.
// themeLayers.test.ts fails if the two drift apart.
export const LAYERS = {
  sticky: 100,
  drawer: 200,
  header: 300,
  floating: 400,
  modal: 500,
  popover: 700,
  toast: 800,
  tooltip: 900,
} as const

const theme = createTheme({
  shadows: SHADOWS,
  zIndex: {
    mobileStepper: LAYERS.sticky,
    fab: LAYERS.floating,
    speedDial: LAYERS.floating,
    appBar: LAYERS.header,
    drawer: LAYERS.drawer,
    modal: LAYERS.modal,
    snackbar: LAYERS.toast,
    tooltip: LAYERS.tooltip,
  },
  palette: {
    // Primary brand is near-black; MUI chrome (tabs, badges, checkboxes,
    // contained buttons, etc.) inherits this. atlas-blue lives on `info`.
    primary: {
      main: '#292a2d',
      contrastText: '#fff',
    },
    secondary: {
      main: '#1A202C',
    },
    // atlas-blue — used for links (MuiLink defaults to this) and any
    // intentionally-blue accent via color="info".
    info: {
      main: '#0066f5',
    },
    // Bridge MUI surfaces to the semantic token layer so Paper-based components
    // (Paper, Menu, Popover, Dialog, TableContainer) follow light/dark color mode.
    background: {
      default: 'var(--surface-page)',
      paper: 'var(--surface-default)',
    },
  },
  spacing: 8,
  // Custom breakpoints aligned with our responsive SCSS mixins
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 600, // Small devices (portrait tablets)
      md: 960, // Medium devices (landscape tablets)
      lg: 1280, // Large devices (desktops)
      xl: 1920, // Extra large devices (large desktops)
    },
  },
  typography: {
    fontFamily,
    fontSize: 14,
    fontWeightLight: 400,
    fontWeightBold: 600,
    body1: {
      fontSize: 'var(--font-size-12)',
      '@media (max-width:600px)': {
        fontSize: 'var(--font-size-14)', // Larger text on mobile for better readability
      },
    },
    body2: {
      fontSize: 'var(--font-size-14)',
    },
    subtitle1: {
      fontSize: 'var(--font-size-18)',
      fontWeight: 600,
    },
    // Make sure buttons and interactive elements have sufficient size on mobile
    button: {
      '@media (max-width:600px)': {
        fontSize: 'var(--font-size-14)',
        padding: 'var(--space-8) var(--space-16)',
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '@media (max-width:600px)': {
            '-webkit-tap-highlight-color': 'transparent', // Remove touch highlight on mobile
          },
        },
        // Ensure proper viewport meta tag is present
        html: {
          '@media (max-width:600px)': {
            scrollBehavior: 'smooth',
          },
        },
      },
    },
    // Responsive Paper component for card views on mobile
    // Links default to atlas-blue (`info`), but the MUI palette is frozen at
    // light-mode values (dark mode is CSS-token-only), so a raw `info` link
    // stays #0066f5 in dark mode and is hard to read. Bridge default/`info`
    // links to the --text-link token (#0066f5 light, #5e8eff dark). styleOverrides
    // apply as plain CSS, so a var() is safe here. Explicit color props
    // (error, etc.) are left untouched.
    MuiLink: {
      defaultProps: {
        color: 'info',
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'info' && {
            color: 'var(--text-link)',
            '&:hover': {
              color: 'var(--text-link-hover)',
            },
          }),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Token-driven so Paper (and Menu/Popover/Dialog/TableContainer that
          // build on it) honor dark mode instead of MUI's hardcoded white.
          backgroundColor: 'var(--surface-default)',
          color: 'var(--text-primary)',
          '@media (max-width:600px)': {
            borderRadius: 'var(--radius-surface)',
          },
        },
      },
    },
    // Touch targets, keyed on the pointer rather than the viewport width. A 1280px
    // tablet is a coarse pointer and needs the floor; a 500px-wide desktop window is a
    // mouse and does not. The old max-width:600px also left the 600-960px band — where
    // the mobile chrome is already active — with no floor at all.
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '@media (pointer: coarse)': {
            minHeight: 'var(--size-lg)',
            minWidth: 'var(--size-lg)',
          },
        },
      },
    },
    // Atlas buttons carry their own press state (`:active` in
    // Common/Button/Button.module.scss), so the ripple is a second, slower piece of
    // feedback for the same click. Scoped to MuiButton on purpose: MuiButtonBase
    // would also strip it from MenuItems, IconButtons, Chips, checkboxes, radios and
    // tabs, none of which get a replacement press state.
    //
    // The focus ring lives here, not in Button.module.scss, because the ripple it
    // replaces was MUI's — every MuiButton had it, including the handful that import
    // `@mui/material/Button` directly (Documents, PlanProgressPanel,
    // SubagentStatusPanel, ExpandButton, PresetButton, the two Arc chat tables) and
    // so never receive the common Button's `.root` rules at all. A glow defined
    // only in that module would have left all of them with no keyboard focus
    // indication once the ripple went.
    // `Mui-focusVisible` is ButtonBase's own `:focus-visible` detection, so a mouse
    // click still leaves no glow behind.
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': FOCUS_GLOW,
          // Press, for the same reason as the glow above: the ripple was the only
          // press feedback the buttons that never receive `Button.module.scss`'s
          // `.root` had. A `currentColor` wash is the one tint that works without
          // knowing the button's palette — every raw `@mui/material/Button` call
          // site sets a `color` and leaves the background transparent, so it reads
          // as a press on all of them and adapts to both color modes.
          //
          // This sits at (0,2,0), the same specificity as the module's own
          // `.root:active` / `.secondary:active` / `.error:active`, and
          // `<StyledEngineProvider injectFirst>` puts the module sheet last, so
          // every Atlas variant keeps its own press color and only the buttons with
          // no press state of their own land here.
          '&:active': {
            backgroundColor: 'color-mix(in srgb, currentColor 12%, transparent)',
          },
        },
      },
    },
    // Tooltips do not open from a touch. On a phone they are noise at best — a tap on
    // any control with one popped a panel over the thing just tapped, and the long press
    // that opens a message's action sheet also triggered them — and there is no hover to
    // dismiss them with. Nothing changes on a desktop: the touch listener is the only one
    // switched off, so hover still works, and keyboard focus still works because MUI
    // gates the focus listener on `:focus-visible`, which a tap does not set.
    //
    // Two tooltips opt back in with `disableTouchListener={false}`, both in the dense
    // mobile table (Common/TableFiltersRow and Common/TableCell): they expand an
    // abbreviated column header, so they exist *for* the device that abbreviates it, and
    // without them a header reading "TOT B…" has no way to be read at all.
    MuiTooltip: {
      defaultProps: {
        disableTouchListener: true,
      },
      styleOverrides: {
        tooltip: { borderRadius: 'var(--radius-tag)' },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disableScrollLock: true,
        },
      },
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minHeight: 'var(--size-lg)',
          },
        },
        // Dropdown arrow defaults to palette.action.active (hardcoded dark),
        // invisible in dark mode. Bridge to the token layer.
        icon: {
          color: 'var(--text-secondary)',
          // MUI freezes the disabled arrow at a light-mode palette value with
          // higher specificity than the base rule above, so a disabled Select's
          // arrow goes invisible on dark surfaces — the read-only assignee
          // select on the LCR / Amendment pages, and the Deal Summary status
          // pill, which every past deal renders read-only. Bridge it too.
          '&.Mui-disabled': {
            color: 'var(--text-disabled)',
          },
        },
      },
    },
    // Icon buttons default to palette.action.active and color="primary" to
    // palette.primary.main — both frozen at light-mode darks (dark mode is
    // CSS-token-only, the MUI palette never flips), so the icons vanish on dark
    // surfaces. Map to tokens via ownerState; we can't touch the palette itself
    // because MUI runs primary/action through alpha() (a var() string throws).
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'default') && {
            color: 'var(--text-secondary)',
          }),
          ...(ownerState.color === 'primary' && {
            color: 'var(--action-primary)',
          }),
          '&.Mui-disabled': {
            color: 'var(--text-disabled)',
          },
          // Same glow MuiButton gets above. Icon buttons kept MUI's ripple, which
          // is a press effect, not a focus indicator — so until now none of them
          // showed anything at all on keyboard focus.
          '&.Mui-focusVisible': FOCUS_GLOW,
        }),
      },
    },
    // Standalone icons with color="primary" pull palette.primary.main (frozen
    // dark) — invisible on dark surfaces (e.g. the automation clock in the chat
    // sidebar). Bridge to the token, which matches in light and flips in dark.
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'primary' && {
            color: 'var(--action-primary)',
          }),
        }),
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        // MUI renders menus, selects and popovers as modals; the popover layer keeps them usable
        // inside a dialog.
        root: { zIndex: LAYERS.popover },
        paper: floatingSurface('var(--elevation-overlay)', 'var(--radius-surface)'),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ ownerState }) =>
          ownerState.fullScreen
            ? {}
            : floatingSurface('var(--elevation-modal)', 'var(--radius-modal)'),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }) =>
          !ownerState.variant || ownerState.variant === 'temporary'
            ? { boxShadow: 'var(--elevation-modal)' }
            : {},
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: floatingSurface('var(--elevation-overlay)', 'var(--radius-surface)'),
      },
    },
    MuiPickerPopper: {
      styleOverrides: {
        root: { zIndex: LAYERS.popover },
        paper: floatingSurface('var(--elevation-overlay)', 'var(--radius-surface)'),
      },
    },
    // Plain Select/Menu items (e.g. Common/Select) have no per-component menu
    // styling, so hover/selected fall back to MUI's frozen palette.action.* —
    // translucent BLACK (rgba(0,0,0,0.04 / 0.08)) that reads as a dark smear on
    // the dark menu surface. Bridge to tokens so hover is a legible wash, selected
    // carries the accent treatment, and the highlight is rounded to match the field.
    // Components with their own menu classes (SelectField) win on specificity.
    MuiMenuItem: {
      styleOverrides: {
        // minHeight, not height, so an item with a wrapping label still grows.
        // MUI resets its own minHeight to `auto` from `sm` up, and stylis hoists
        // every media block below the flat declarations of the same class — so a
        // plain `minHeight` here loses on desktop and the scale must be restated
        // inside the breakpoint to land. The coarse-pointer floor MuiButtonBase
        // sets above lands in a different class at the same specificity, where
        // injection order would decide it; restating it here settles the tie.
        root: ({ theme: muiTheme, ownerState }) => {
          // A `dense` menu asked for a compact row — that is --size-sm, also on the scale.
          const rowHeight = ownerState.dense ? 'var(--size-sm)' : 'var(--size-md)'

          return {
            minHeight: rowHeight,
            [muiTheme.breakpoints.up('sm')]: { minHeight: rowHeight },
            // Must follow the `sm` block: a coarse-pointer tablet matches both.
            '@media (pointer: coarse)': { minHeight: 'var(--size-lg)' },
            borderRadius: 'var(--radius-control)',
            '&:hover': { backgroundColor: 'var(--surface-hover)' },
            '&.Mui-focusVisible': { backgroundColor: 'var(--surface-hover)' },
            '&.Mui-selected': {
              ...selectedOptionStyle,
              '&:hover, &.Mui-focusVisible': {
                backgroundColor: 'var(--action-accent-soft)',
              },
            },
          }
        },
      },
    },
    // The Autocomplete dropdown's message slots are styled straight off the
    // frozen palette — "No options"/`noOptionsText` and the loading row take
    // palette.text.secondary (near-black), the group header takes
    // palette.background.paper (white). On the dark menu surface that's black
    // text on near-black and a white header band; these slots have no
    // component-level styling to win, so bridge them here for every Autocomplete.
    // Option rows get their hover/selected fill from palette.action.*, which is
    // frozen at rgba(0,0,0,0.04) — a *darkening*, so on a dark surface the
    // highlight either vanishes or moves the wrong way. The option rules ride on
    // the `listbox` slot because its overrides land after MUI's own option rules
    // in the same class, so the equal-specificity `.Mui-focusVisible` ties go to us.
    MuiAutocomplete: {
      styleOverrides: {
        popper: { zIndex: LAYERS.popover },
        paper: floatingSurface('var(--elevation-overlay)', 'var(--radius-surface)'),
        noOptions: { color: 'var(--text-secondary)' },
        loading: { color: 'var(--text-secondary)' },
        groupLabel: {
          backgroundColor: 'var(--surface-default)',
          color: 'var(--text-secondary)',
        },
        listbox: {
          '& .MuiAutocomplete-option': {
            '&:hover, &.Mui-focused': {
              backgroundColor: 'var(--surface-hover)',
            },
            '&[aria-selected="true"]': {
              ...selectedOptionStyle,
              '&:hover, &.Mui-focused': {
                backgroundColor: 'var(--action-accent-soft)',
              },
            },
          },
        },
      },
    },
    // Bare chips (color="default") use palette.text.primary for their label —
    // frozen dark, unreadable on dark surfaces. Bridge default chips to the token;
    // colored chips (primary/error/etc.) keep their own contrastText.
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'default') && {
            color: 'var(--text-primary)',
          }),
        }),
        // MUI paints the delete (X) icon with a hardcoded near-black
        // (rgba(0,0,0,0.26)) that ignores the palette — invisible on dark
        // surfaces. Bridge default chips to the token; colored chips (error,
        // etc.) keep MUI's contrast handling.
        deleteIcon: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'default') && {
            color: 'color-mix(in srgb, var(--text-primary) 45%, transparent)',
            '&:hover': {
              color: 'color-mix(in srgb, var(--text-primary) 70%, transparent)',
            },
          }),
        }),
      },
    },
    // Bridge Typography's text-color props to the token layer. `color="textSecondary"`
    // (used widely, and by ListItemText's secondary text) resolves to MUI's hardcoded
    // palette.text.*, which stays near-black and is unreadable in dark mode. Keyed on
    // ownerState so we never mutate palette.text — MUI runs that through alpha() in
    // Button/Chip/Link/etc., and a var() string there throws.
    MuiTypography: {
      styleOverrides: {
        // Bridge BOTH spellings to the token layer: the legacy system prop
        // (`color="textSecondary"`) and the theme-path prop (`color="text.secondary"`).
        // MUI resolves the path form straight to the frozen light palette, so without
        // this branch it stays near-black and is unreadable in dark mode. Keying on
        // ownerState (never mutating palette.text) keeps alpha()-based components safe.
        root: ({ ownerState }) => ({
          ...((ownerState.color === 'textPrimary' || ownerState.color === 'text.primary') && {
            color: 'var(--text-primary)',
          }),
          ...((ownerState.color === 'textSecondary' || ownerState.color === 'text.secondary') && {
            color: 'var(--text-secondary)',
          }),
          ...((ownerState.color === 'textDisabled' || ownerState.color === 'text.disabled') && {
            color: 'var(--text-disabled)',
          }),
        }),
      },
    },
    // Bridge disabled-input text to the token layer. MUI paints disabled text
    // via `-webkit-text-fill-color: palette.text.disabled` (hardcoded near-black),
    // which wins over `color` and is unreadable in dark mode.
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Raw MUI inputs color their text from palette.text.primary (frozen
          // near-black) — invisible on dark surfaces. Bridge to the token; any
          // component that sets its own input color (TextField, CurrencyField,
          // Autocomplete) still wins via its className.
          color: 'var(--text-primary)',
          '&::placeholder': {
            color: 'var(--text-tertiary)',
            opacity: 1,
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: 'var(--text-disabled)',
          },
        },
      },
    },
    // The outlined variant draws its border as a separate `notchedOutline`
    // fieldset coloured from palette.divider / palette.text.primary — both frozen
    // at light values, so on a dark surface the border is invisible and the
    // control reads as absent entirely. MuiInputBase above only bridges the
    // *text*, which is why an outlined field could still look empty.
    //
    // Most inputs here go through Common/TextField (standard variant, token SCSS)
    // and never hit this. It exists so a raw `<TextField />` dropped in by
    // someone who doesn't know that isn't silently broken in dark mode.
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'var(--border-default)',
        },
        root: {
          borderRadius: 'var(--radius-control)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border-strong)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border-focus)',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--status-error)',
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--border-subtle)',
          },
        },
      },
    },
    // Token-driven so table text honors dark mode instead of MUI's hardcoded
    // palette.text.* (which stays near-black and is unreadable in dark mode).
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)',
        },
        footer: {
          color: 'var(--text-secondary)',
        },
      },
    },
    // Sort labels: the active (sorted) column uses palette.text.primary (frozen
    // near-black) for both label and arrow — invisible on dark headers. Bridge
    // to tokens so the sorted column reads in dark mode.
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          '&:hover': { color: 'var(--text-primary)' },
          '&.Mui-active': {
            color: 'var(--text-primary)',
            '& .MuiTableSortLabel-icon': { color: 'var(--text-primary)' },
          },
        },
        icon: { color: 'var(--text-secondary)' },
      },
    },
    // Ensure tables can scroll horizontally on mobile
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            overflowX: 'auto',
          },
        },
      },
    },
    // Date-picker popup (@mui/x-date-pickers) — the calendar's day cells, header
    // label and weekday labels default to MUI's frozen light palette, so they are
    // invisible on the dark popup. Bridge them all to the semantic token layer.
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)',
          '&:hover': {
            backgroundColor: 'var(--surface-subtle)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--action-accent)',
            color: 'var(--surface-default)',
            '&:hover, &:focus': {
              backgroundColor: 'var(--action-accent)',
            },
          },
          '&.MuiPickersDay-today': {
            borderColor: 'var(--action-accent)',
          },
          // Match the library's own selector specificity (`&.Mui-disabled:not(.Mui-selected)`,
          // PickersDay.js). A plain `&.Mui-disabled` here loses the cascade, so disabled days
          // fall back to MUI's light-palette `text.disabled` (near-black) on the dark calendar.
          '&.Mui-disabled:not(.Mui-selected)': {
            color: 'var(--text-disabled)',
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: 'var(--text-secondary)',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: 'var(--text-primary)',
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersYear-yearButton': {
            color: 'var(--text-primary)',
            '&.Mui-selected': {
              backgroundColor: 'var(--action-accent)',
              color: 'var(--surface-default)',
              '&:hover, &:focus': {
                backgroundColor: 'var(--action-accent)',
              },
            },
          },
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersMonth-monthButton': {
            color: 'var(--text-primary)',
            '&.Mui-selected': {
              backgroundColor: 'var(--action-accent)',
              color: 'var(--surface-default)',
              '&:hover, &:focus': {
                backgroundColor: 'var(--action-accent)',
              },
            },
          },
        },
      },
    },
    // --- Dark-mode token bridges -------------------------------------------
    // Dark mode is CSS-token-only (the MUI palette never flips), so any component
    // that colors itself from palette.text.* / palette.primary / palette.action /
    // palette.divider stays frozen at light values and breaks on dark surfaces.
    // Each override below maps those frozen colors to the semantic token layer.
    // Token light values equal the old frozen values, so light mode is unchanged.

    // Selection controls — checked color is palette.primary.main (near-black),
    // invisible on dark; unchecked uses action.active (also frozen).
    MuiCheckbox: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color ||
            ownerState.color === 'default' ||
            ownerState.color === 'primary') && {
            color: 'var(--text-secondary)',
            '&.Mui-checked': { color: 'var(--action-primary)' },
          }),
          '&.Mui-disabled': { color: 'var(--text-disabled)' },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color ||
            ownerState.color === 'default' ||
            ownerState.color === 'primary') && {
            color: 'var(--text-secondary)',
            '&.Mui-checked': { color: 'var(--action-primary)' },
          }),
          '&.Mui-disabled': { color: 'var(--text-disabled)' },
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: 'var(--action-primary)' },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'var(--action-primary)',
          },
        },
        track: { backgroundColor: 'var(--border-strong)' },
      },
    },
    // Divider/border use palette.divider (frozen faint black) — invisible on dark.
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'var(--border-default)' },
      },
    },
    // Progress indicators default to palette.primary (near-black) — token accent
    // is the role for progress and stays visible in both modes.
    MuiCircularProgress: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'primary') && {
            color: 'var(--action-accent)',
          }),
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'primary') && {
            backgroundColor: 'var(--action-accent-soft)',
          }),
        }),
        bar: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'primary') && {
            backgroundColor: 'var(--action-accent)',
          }),
        }),
      },
    },
    // Tabs — label text.secondary, selected/indicator primary (all frozen).
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          '&.Mui-selected': { color: 'var(--action-accent)' },
          '&.Mui-disabled': { color: 'var(--text-disabled)' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: 'var(--action-accent)' },
      },
    },
    // Stepper — icons/labels/connector all pull frozen palette colors.
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: 'var(--border-default)',
          '&.Mui-active': { color: 'var(--action-accent)' },
          '&.Mui-completed': { color: 'var(--action-accent)' },
        },
        text: { fill: 'var(--text-inverse)' },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: 'var(--text-secondary)',
          '&.Mui-active': { color: 'var(--text-primary)' },
          '&.Mui-completed': { color: 'var(--text-primary)' },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: { borderColor: 'var(--border-default)' },
      },
    },
    // List rows — icon + secondary text use text.secondary (frozen).
    MuiListItemIcon: {
      styleOverrides: {
        root: { color: 'var(--text-secondary)' },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        secondary: { color: 'var(--text-secondary)' },
      },
    },
    // Form label/helper text — text.secondary / error.main (frozen).
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: 'var(--text-primary)',
          '&.Mui-disabled': { color: 'var(--text-disabled)' },
        },
      },
    },
    // Also covers InputLabel: MUI builds it as styled(FormLabel), so these reach
    // every floating label too — no separate MuiInputLabel override needed.
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          '&.Mui-focused': { color: 'var(--action-accent)' },
          '&.Mui-error': { color: 'var(--status-error)' },
          '&.Mui-disabled': { color: 'var(--text-disabled)' },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          '&.Mui-error': { color: 'var(--status-error)' },
        },
      },
    },
    // Alert — standard variants use lightened palette bg + darkened text, both
    // frozen at light values; map to the status token pairs (which flip in dark).
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: 'var(--status-success-bg)',
          color: 'var(--status-success-text)',
        },
        standardError: {
          backgroundColor: 'var(--status-error-bg)',
          color: 'var(--status-error-text)',
        },
        standardWarning: {
          backgroundColor: 'var(--status-warning-bg)',
          color: 'var(--status-warning-text)',
        },
        standardInfo: {
          backgroundColor: 'var(--status-info-bg)',
          color: 'var(--status-info-text)',
        },
        icon: { color: 'inherit' },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { color: 'var(--text-secondary)' },
        separator: { color: 'var(--text-tertiary)' },
      },
    },
    // Accordion expand chevron defaults to action.active (frozen) — invisible on dark.
    MuiAccordionSummary: {
      styleOverrides: {
        expandIconWrapper: { color: 'var(--text-secondary)' },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        // The frozen palette's border and selected tint vanish against the surface.
        root: {
          color: 'var(--text-secondary)',
          backgroundColor: 'var(--surface-default)',
          borderColor: 'var(--border-default)',
          '&:hover': { backgroundColor: 'var(--surface-hover)' },
          '&.Mui-selected': {
            color: 'var(--action-accent)',
            backgroundColor: 'var(--action-accent-subtle)',
          },
          '&.Mui-selected:hover': { backgroundColor: 'var(--action-accent-soft)' },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary)',
          '&.Mui-selected': { backgroundColor: 'var(--action-accent-subtle)' },
        },
      },
    },
    // Avatar default bg is palette.grey (frozen light grey) — bridge to a token.
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--surface-active)',
          color: 'var(--text-secondary)',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'default') && {
            backgroundColor: 'var(--action-primary)',
            color: 'var(--text-inverse)',
          }),
        }),
      },
    },
    // Skeleton shimmer base is rgba(0,0,0,0.11) (frozen) — nearly invisible on
    // dark surfaces; use a token that reads in both modes.
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: 'var(--surface-active)' },
      },
    },
    // No global MuiDrawer width override. There used to be one — `width: 100%` capped at
    // `maxWidth: 320px` below 960px — written for the top-anchored mobile navigation drawer,
    // which no longer exists: the bottom tab bar replaced it. What it left behind was a rule
    // reaching every Drawer in the app through `.MuiDrawer-root .MuiDrawer-paper` (0,2,0),
    // which out-specifies any single class a component stylesheet can bring, and a `max-width`
    // no component-level `width: 100%` can undo.
    //
    // Only four Drawers remain, and the cap was wrong for all of them on a phone: MobileNav's
    // More sheet and MobileControlsDrawer are bottom sheets (full-bleed by definition), while
    // TaskDetailDrawer and RightSidebar both ask for `width: 100%` below md in their own
    // stylesheets and were silently held at 320px — which is why the task panel kept rendering
    // as a 320px slab over the table however many times its own width was set.
  },
})

// body1, body2 and subtitle1 are sized by `var()` tokens, which responsiveFontSizes can't parse.
export default responsiveFontSizes(theme, {
  variants: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle2', 'caption', 'button', 'overline'],
})
